/**
 * Support Resolver — MeesterJay 4.1
 *
 * §1.2B STABILIZATION: Unified resolution with priority logic.
 *
 * Two resolution paths:
 * 1. `resolveSupportContent(question)` — Legacy path used by PracticeSession/FeedbackDisplay.
 *    Resolves from question.supportAids/feedback → family registry → fallback.
 * 2. `resolveUnifiedSupport(familyId)` — Canonical path (§1.2B).
 *    Resolves family-level support straight from the registry. The old
 *    `seedSupport` parameter and `SeedSupportDefinition` type were removed in
 *    the Support RC audit (Ronde 3, finding S-18/S-22): no seed JSON ever
 *    passed a second argument, and the family-level steps/afterSummary
 *    branches were hardcoded to `undefined` by the bridge, so the entire
 *    override path was dead. If per-tier or per-seed overrides are needed in
 *    the future (see CHEATSHEET_HINT_SYSTEM_REDESIGN_2026.md §4.1), add a
 *    typed parameter with real callers.
 *
 * Naming convention (§1.2C):
 *   familySupportRegistry.defaultExplanation → resolved explanation.body
 *   familySupportRegistry.hints[]            → resolved hints[]
 *
 * NOTE: The legacy `cheatSheet` object was removed in 2026-05. The student-facing
 * Spiekbriefje is now powered entirely by hints[] + defaultExplanation.
 */
import { Question } from '../content';
import { VisualAid } from '../visuals';
import { HintStep } from './hints';
import { FAMILY_SUPPORT_REGISTRY, getFamilySupportEntry } from './familySupportRegistry';
import type { ResolvedSupport, ExplanationContent, SupportCoverage } from './types';
import { calculateCoverage as calcCoverage } from './types';

// ─── Question-level resolved support ─────────────────────────────────────────
// Used by `resolveSupportContent()` — the per-question resolution path.
// For the canonical family-level resolution, use `resolveUnifiedSupport()` → `ResolvedSupport`.

export interface QuestionResolvedSupport {
  hints: HintStep[];
  visual?: VisualAid;
  explanation?: string;
  source: 'question' | 'family' | 'representation' | 'none';
  solutionSteps?: string[];
}

/**
 * Deterministically resolve support content for a question.
 *
 * Resolution Order:
 * 1. Question-level overrides (if provided in seed)
 * 2. Representation-specific support (if representationId matches)
 * 3. Family-level default support
 * 4. Fallback (empty)
 *
 * STAGE 3: Enhanced to support new feedback structure
 *
 * @returns QuestionResolvedSupport — the per-question format used by FeedbackDisplay/ExplanationCard
 */
export function resolveSupportContent(question: Question): QuestionResolvedSupport {
  const familyId = question.familyId;
  const registryEntry = FAMILY_SUPPORT_REGISTRY[familyId];

  // Check for representationId in question data
  const representationId = question.data?.representationId as string | undefined;
  const representationSupport = representationId && registryEntry?.representations?.[representationId];

  // Resolve Hints
  let hints: HintStep[] = [];
  if (representationSupport?.hints) {
    hints = representationSupport.hints;
  } else if (registryEntry?.hints) {
    hints = registryEntry.hints;
  }

  // STAGE 3: Resolve Visual from canonical supportAids + family fallbacks
  let visual: VisualAid | undefined;

  // Check new supportAids structure first
  if (question.supportAids && question.supportAids.length > 0) {
    visual = question.supportAids[0]; // Use first support visual
  } else {
    // Family/representation fallback
    if (representationSupport?.visual) {
      visual = representationSupport.visual;
    } else if (registryEntry?.defaultVisual) {
      visual = registryEntry.defaultVisual;
    }
  }

  // Resolve explanation from canonical question.feedback + registry fallback
  let explanation: string | undefined;
  let solutionSteps: string[] | undefined;
  if (question.feedback) {
    explanation = question.feedback.explanationText;
    solutionSteps = question.feedback.solutionSteps;
  } else {
    // Family/representation fallback
    if (representationSupport?.explanation) {
      explanation = representationSupport.explanation;
    } else if (registryEntry?.defaultExplanation) {
      explanation = registryEntry.defaultExplanation;
    }
  }

  // Determine source for debugging/reporting
  let source: QuestionResolvedSupport['source'] = 'none';
  if (question.supportAids || question.feedback) {
    source = 'question';
  } else if (representationSupport) {
    source = 'representation';
  } else if (registryEntry) {
    source = 'family';
  }
  return {
    hints,
    visual,
    explanation,
    solutionSteps,
    source
  };
}

/**
 * Unified support resolution (§1.2B).
 *
 * Reads family-level support directly from FAMILY_SUPPORT_REGISTRY and shapes
 * it into a `ResolvedSupport` with coverage diagnostics. There is no longer a
 * seed-level override layer — see file header for the rationale (S-18/S-22).
 *
 * @param familyId - The canonical family ID
 * @returns ResolvedSupport from types.ts (canonical format)
 */
export function resolveUnifiedSupport(familyId: string): ResolvedSupport {
  const registryEntry = getFamilySupportEntry(familyId);
  const sources: ('family' | 'seed' | 'override')[] = [];

  // --- Explanation (family-level "Zo werkt het") ---
  let explanation: ExplanationContent | null = null;
  const explanationText = registryEntry?.defaultExplanation;
  if (explanationText) {
    explanation = {
      title: 'Zo werkt het',
      body: explanationText,
      source: 'family'
    };
    sources.push('family');
  }

  // --- Hints (family-level) ---
  // F-H1: Return lossless HintStep[] so downstream consumers (admin preview,
  // future tier-aware UI) keep title + visual + hintType.
  const hints: HintStep[] = registryEntry?.hints && registryEntry.hints.length > 0 ? registryEntry.hints : [];

  // --- Coverage ---
  // S-17 (Ronde 4): `steps` and `afterSummary` are gone from the
  // ResolvedSupport contract. The only fields the resolver populates from
  // the family registry are `explanation` and `hints`, so coverage tracks
  // exactly those two.
  const coverageBase = {
    hasExplanation: !!explanation,
    hasHints: hints.length > 0
  };
  const coverage: SupportCoverage = {
    ...coverageBase,
    completeness: calcCoverage(coverageBase),
    sources
  };
  return {
    explanation,
    hints,
    coverage
  };
}